import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  InputGroup,
  Row,
} from "react-bootstrap";
import DateRangePicker from "../../../../common/CustomDateRangePicker";
import CustomPagination from "../../../../common/Pagination";
import TableRowsPerPage from "../../../../common/TableRowsPerPage";
import BillingDataTable from "../../Billing/components/BillingDataTable";
import {
  getTeamsBillingSearchList,
  getTeamsBillingSummary,
} from "../../../../services/BillingAPI";
import {
  BILLING_DASHBOARD_HEADERS,
  BILLING_SORT_KEY_MAP,
} from "../action/constant";
import useAnimatedCounter from "../../../ToDos/useAnimatedCounter";
import AddUpdateBilling from "./AddUpdateBilling";
import {
  getBillingConfigurationGrid,
  getBillingConfigurationGridView,
  getCaseLevelBillingList,
  getCaseLevelMasterBillingList,
} from "../../../../services/BillingConfigApi";
import { BILLING_DASHBOARD_CASE_HEADERS } from "../../Billing/actions/constant";
import { useSelector } from "react-redux";
import { RootState } from "../../../../redux/Store";

const TeamsBillingDashboard = ({
  teamId,
  showAddUpdateBilling,
  activeTab,
  onCloseBilling,
  setShowEditBilling,
}: any) => {
  const HIDDEN_COLUMNS_BY_CARD: Record<string, string[]> = {
    "Master Billing List": [
      "teamName",
      "clientInformation",
      "patientName",
      "accountNo",
      "caseId",
      "rate",
      "thresholdType",
    ],
    "Team Billing List": [
      "caseId",
      "chargesName",
      "patientName",
      "accountNo",
      "effectiveTo",
      "status",
      "lastUpdatedOn",
      "thresholdType",
      "rate",
    ],
    "Case Billing List": [
      "billingType",
      "teamName",
      "clientInformation",
      "teamName",
      "clientName",
      "chargesName",
      "priority",
      "thresholdType",
      "rate",
      "effectiveFrom",
      "effectiveTo",
    ],
  };
  const loggedInUserid = useSelector((state: RootState) => state.auth.userid);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [activeCard, setActiveCard] = useState<string>("Master Billing List");
  const [activeFilter, setActiveFilter] = useState("SYSTEM");
  const [action, setAction] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [configurationData, setConfigurationData] = useState<any[]>([]);
  const [selectedConfigurationData, setSelectedConfigurationData] =
    useState<any>(null);
  const [masterBillingCount, setMasterBillingCount] = useState<string>("");
  const [teamBillingCount, setTeamBillingCount] = useState<string>("");
  const [caseBillingCount, setCaseBillingCount] = useState<string>("");
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [totalRows, setTotalRows] = useState<any>("6");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(15);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [sortColumn, setSortColumn] = useState<string>("");
  const [sortOrder, setSortOrder] = useState<string>("DESC");
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);

  const [dateRange, setDateRange] = useState<
    [Date | undefined, Date | undefined]
  >([firstDayOfMonth, lastDayOfMonth]);

  const getApiSortColumn = (
    uiKey: string,
    billingType: "SYSTEM" | "TEAM" | "CASE",
  ) => {
    return BILLING_SORT_KEY_MAP[billingType]?.[uiKey] || "";
  };

  const handleDateRangeChange = (newRange: [Date | null, Date | null]) => {
    const [start, end] = newRange;
    setDateRange([start ?? undefined, end ?? undefined]);
  };

  const handleResetDate = () => {
    setDateRange([firstDayOfMonth, lastDayOfMonth]);
  };

  const toLocalISOString = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const billingApiMap: Record<string, Function> = {
    SYSTEM: getBillingConfigurationGridView,
    TEAM: getTeamsBillingSearchList,
    CASE: getCaseLevelMasterBillingList,
  };

  useEffect(() => {
    if (showAddUpdateBilling) {
      setOpenDrawer(true);
    }
  }, [showAddUpdateBilling]);

  const getConfigurationSummaryData = async (query: string = "") => {
    try {
      const response = await getTeamsBillingSummary(teamId);
      if (response) {
        setMasterBillingCount(String(response?.countOfMasterBilling) ?? "");
        setTeamBillingCount(String(response?.countOfTeamBilling) ?? "");
        setCaseBillingCount(String(response?.countOfCaseBilling) ?? "");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getConfigurationSummaryData();
  }, []);

  const getBilingConfiurationData = async (
    query: string = "",
    action: string = "",
    pageNo: number = 1,
    pageSize: number = itemsPerPage,
    key: string = sortColumn,
    direction: string = sortOrder,
  ) => {
    try {
      setLoading(true);
      const trimmedQuery = query?.trim();
      const [start, end] = dateRange;
      const isMasterBilling = activeFilter === "SYSTEM";
      const payload = {
        teamId: teamId,
        caseInfoId: null,
        searchString: trimmedQuery || "",
        startDate: isMasterBilling && start ? toLocalISOString(start) : null,
        endDate: isMasterBilling && end ? toLocalISOString(end) : null,
        pageNo,
        pageSize,
        sortColumn: key,
        sortOrder: direction,
        loggedInUserId: loggedInUserid,
      };

      const apiFn = billingApiMap[activeFilter];
      const response = apiFn ? await apiFn(payload) : [];
      setConfigurationData(response);
      setTotalPages(response[0]?.totalPages || 1);
      setTotalRows(response[0]?.totalRecords || null);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const [start, end] = dateRange;

    if (start && end) {
      getBilingConfiurationData(
        searchQuery,
        activeFilter,
        currentPage,
        itemsPerPage,
        sortColumn,
        sortOrder,
      );
    }
  }, [
    dateRange,
    searchQuery,
    currentPage,
    itemsPerPage,
    sortColumn,
    sortOrder,
  ]);

  const debounce = (func: Function, delay: number) => {
    let timer: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timer);
      timer = setTimeout(() => func(...args), delay);
    };
  };

  const handleInputChange = debounce((value: string) => {
    if (value.length >= 3 || value.length === 0) {
      setSearchQuery(value);
      setCurrentPage(1);
    }
  }, 300);

  const handleResetFilters = () => {
    if (inputRef.current) {
      inputRef.current.value = "";
    }

    setSearchQuery("");
  };

  useEffect(() => {
    if (!activeFilter) return;
    const [start, end] = dateRange;
    const filterQuery = searchQuery || "";
    getBilingConfiurationData(
      filterQuery,
      activeFilter,
      1,
      itemsPerPage,
      sortColumn,
      sortOrder,
    );
  }, [activeFilter, dateRange]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target === inputRef.current) {
        return;
      }

      if (e.key === "Backspace" || e.key === "Delete") {
        setDateRange([undefined, undefined]);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const paginate = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    getBilingConfiurationData(
      searchQuery,
      activeFilter,
      pageNumber,
      itemsPerPage,
      sortColumn,
      sortOrder,
    );
  };

  const handleSortAction = (row: any) => {
    const apiSortColumn = getApiSortColumn(
      row.key,
      activeFilter as "SYSTEM" | "TEAM" | "CASE",
    );

    setSortColumn(apiSortColumn);
    setSortOrder(row.direction);
    getBilingConfiurationData(
      searchQuery,
      activeFilter,
      currentPage,
      itemsPerPage,
      apiSortColumn,
      row.direction,
    );
  };

  const handleRowsPerPageChange = (value: number) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  const handleCardClick = (filter: string) => {
    const current = 1;
    const perPage = 15;

    setActiveFilter(filter);
    setCurrentPage(current);
    setItemsPerPage(perPage);
    setSortColumn("");
    setSortOrder("DESC");
  };

  const handleRowAction = (row: any, action: string) => {
    setAction(action);
    setSelectedConfigurationData(row);
    setOpenDrawer(true);
    setShowEditBilling(true);
  };

  const handleClose = (billingType: "SYSTEM" | "TEAM" | "CASE") => {
    setOpenDrawer(false);
    setSelectedConfigurationData(null);
    setActiveFilter(billingType);
    if (billingType === "SYSTEM") {
      setActiveCard("Master Billing List");
    } else if (billingType === "TEAM") {
      setActiveCard("Team Billing List");
    } else if (billingType === "CASE") {
      setActiveCard("Case Billing List");
    }
    setCurrentPage(1);
    setSortColumn("");
    setSortOrder("DESC");
    getBilingConfiurationData();
    getConfigurationSummaryData();
    if (onCloseBilling) {
      onCloseBilling();
    }
  };

  const filteredHeaders = useMemo(() => {
    const hiddenKeys = HIDDEN_COLUMNS_BY_CARD[activeCard] || [];

    return BILLING_DASHBOARD_HEADERS.filter(
      (header: any) => !hiddenKeys.includes(header.key),
    );
  }, [activeCard]);

  const entriesText = !totalRows
    ? "Showing 0 of 0 entries"
    : `Showing ${(currentPage - 1) * itemsPerPage + 1} to ${Math.min(
        currentPage * itemsPerPage,
        totalRows,
      )} of ${totalRows} entries`;

  const animatedMasterBillingCount = useAnimatedCounter(masterBillingCount);
  const animatedTeamBillingCount = useAnimatedCounter(teamBillingCount);
  const animatedCaseBillingCount = useAnimatedCounter(caseBillingCount);

  return (
    <div>
      {!openDrawer ? (
        <Container fluid className="px-5">
          <div className="main_wrapper_section appoinment_wrapper mt-3">
            <Container fluid>
              <Row className="g-3">
                <Col md={2}>
                  <Card
                    className={`h-100 todocard4 ${
                      activeCard === "Master Billing List" ? "active" : ""
                    }`}
                    style={{
                      backgroundColor: "#E9E5FE",
                      borderRadius: "5px",
                      border: "1px solid #9C8CEE",
                    }}
                    onClick={() => {
                      handleCardClick("SYSTEM");
                      setActiveCard("Master Billing List");
                    }}
                  >
                    <Card.Body className="d-flex flex-column justify-content-between p-1">
                      <div className="d-flex justify-content-between align-items-center ">
                        <Card.Title
                          style={{
                            color: " #684fe5",
                            fontSize: "14px",
                            fontWeight: "600",
                          }}
                        >
                          Master Billing
                        </Card.Title>
                        <span
                          className="material-symbols-outlined"
                          style={{ color: " #684fe5" }}
                        >
                          settings
                        </span>
                      </div>
                      <div
                        style={{
                          color: " #684fe5",
                          fontSize: "1.1rem",
                          fontWeight: "600",
                        }}
                      >
                        {animatedMasterBillingCount}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={2}>
                  <Card
                    className={`h-100 todocard2 ${
                      activeCard === "Team Billing List" ? "active" : ""
                    }`}
                    style={{
                      backgroundColor: "#FCECBE",
                      borderRadius: "5px",

                      border: "1px solid #E6C872",
                    }}
                    onClick={() => {
                      handleCardClick("TEAM");
                      setActiveCard("Team Billing List");
                    }}
                  >
                    <Card.Body className="d-flex flex-column justify-content-between p-1">
                      <div className="d-flex justify-content-between align-items-center ">
                        <Card.Title
                          style={{
                            color: "#9C6F16",
                            fontSize: "14px",
                            fontWeight: "600",
                          }}
                        >
                          Team Billing
                        </Card.Title>
                        <span
                          className="material-symbols-outlined"
                          style={{ color: "#9C6F16" }}
                        >
                          group
                        </span>
                      </div>
                      <div
                        style={{
                          color: "#9C6F16",
                          fontSize: "1.1rem",
                          fontWeight: "600",
                        }}
                      >
                        {animatedTeamBillingCount}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={2}>
                  <Card
                    className={`h-100 todocard3 ${
                      activeCard === "Case Billing List" ? "active" : ""
                    }`}
                    style={{
                      backgroundColor: "#D6F2FF",
                      borderRadius: "5px",

                      border: "1px solid #99CEE8",
                    }}
                    onClick={() => {
                      handleCardClick("CASE");
                      setActiveCard("Case Billing List");
                    }}
                  >
                    <Card.Body className="d-flex flex-column justify-content-between p-1">
                      <div className="d-flex justify-content-between align-items-center ">
                        <Card.Title
                          style={{
                            color: "#0086ce",
                            fontSize: "14px",
                            fontWeight: "600",
                          }}
                        >
                          Case Billing
                        </Card.Title>
                        <span
                          className="material-symbols-outlined"
                          style={{ color: "#0086ce" }}
                        >
                          flag
                        </span>
                      </div>
                      <div
                        style={{
                          color: "#0086ce",
                          fontSize: "1.1rem",
                          fontWeight: "600",
                        }}
                      >
                        {animatedCaseBillingCount}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
              <div
                className="my-3"
                style={{ background: "#0092D5", height: "2px" }}
              ></div>
              <div className="container-fluid px-2">
                <div className="mt-3 mb-3">
                  <Row className="align-items-center justify-content-between mb-3">
                    <Col xs="auto" className="sectionTitle noborder pe-3">
                      {activeCard}
                    </Col>
                    <Col xs="auto">
                      <div className="d-flex align-items-center gap-2">
                        {activeFilter === "SYSTEM" && (
                          <div
                            className="date-picker-container appointment-data-range customDrodownControlDAtepicker"
                            style={{ width: "280px" }}
                          >
                            <DateRangePicker
                              label=""
                              onChange={handleDateRangeChange}
                              startDate={dateRange[0]}
                              endDate={dateRange[1]}
                              showResetIcon={!!dateRange[0] && !!dateRange[1]}
                              onReset={handleResetDate}
                            />
                          </div>
                        )}

                        <InputGroup className="w-auto">
                          <InputGroup.Text className="px-2">
                            <span className="material-symbols-outlined text-black-50">
                              search
                            </span>
                          </InputGroup.Text>
                          <Form.Control
                            ref={inputRef}
                            aria-label="Search"
                            onChange={(e) =>
                              handleInputChange(e.target.value.trim())
                            }
                            onPaste={(e) => {
                              e.preventDefault();
                              const text = e.clipboardData
                                .getData("text")
                                .trim();
                              handleInputChange(text);
                              if (inputRef.current)
                                inputRef.current.value = text;
                            }}
                          />
                        </InputGroup>
                        <Button
                          className="d-flex align-items-center"
                          style={{
                            background: "none",
                            border: "none",

                            padding: "10px",
                            fontSize: "22px",
                          }}
                          onClick={handleResetFilters}
                        >
                          <span className="material-icons reset_ic0">
                            restart_alt
                          </span>
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </div>
              </div>
              <div className="mb-3 container-fluid">
                <BillingDataTable
                  headers={
                    activeCard == "Case Billing List"
                      ? BILLING_DASHBOARD_CASE_HEADERS
                      : filteredHeaders
                  }
                  rows={configurationData}
                  onSortAction={handleSortAction}
                  onAction={handleRowAction}
                />
              </div>
              <div className="d-flex justify-content-between align-items-center mt-0 mb-4 row p-0 m-0">
                <div className="col-md-12">
                  <div className="row">
                    <div className="col-md-6 showingData_wrapper">
                      <span>{entriesText}</span>
                    </div>
                    <div className="col-md-6">
                      <div className="d-flex flex-row-reverse ">
                        <CustomPagination
                          currentPage={currentPage}
                          totalPages={totalPages}
                          onPageChange={paginate}
                        />
                        <TableRowsPerPage
                          value={itemsPerPage}
                          onSelect={handleRowsPerPageChange}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Container>
          </div>
        </Container>
      ) : null}
      {openDrawer && (
        <AddUpdateBilling
          teamId={teamId}
          onClose={handleClose}
          selectedData={selectedConfigurationData}
        />
      )}
    </div>
  );
};

export default TeamsBillingDashboard;
